/**
 * OS Teams TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys
// =============================================================================

export const teamKeys = {
  all: ['teams'] as const,
  list: () => [...teamKeys.all, 'list'] as const,
  members: (teamId: string) => [...teamKeys.all, 'members', teamId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

export function useTeams() {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: () => api.getTeams(),
  });
}

export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: () => api.getTeamMembers(teamId),
    enabled: !!teamId,
  });
}

export function useAllTeamMembers() {
  return useQuery({
    queryKey: [...teamKeys.all, 'all-members'] as const,
    queryFn: () => api.getAllTeamMembers(),
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; type: string; is_private: boolean }) =>
      api.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team created');
    },
    onError: (error) => {
      toast.error('Failed to create team', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; type?: string; is_private?: boolean } }) =>
      api.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team updated');
    },
    onError: (error) => {
      toast.error('Failed to update team', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.list() });
      toast.success('Team deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete team', { description: getErrorMessage(error) });
    },
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId, role }: { teamId: string; userId: string; role: string }) =>
      api.addTeamMember(teamId, userId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(variables.teamId) });
      toast.success('Member added');
    },
    onError: (error) => {
      toast.error('Failed to add member', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateTeamMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId, role }: { teamId: string; userId: string; role: 'member' | 'leader' }) =>
      api.updateTeamMemberRole(teamId, userId, role),
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(variables.teamId) });
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
      toast.success(`Set as ${variables.role}`);
    },
    onError: (error) => {
      toast.error('Failed to update role', { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      api.removeTeamMember(teamId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(variables.teamId) });
      toast.success('Member removed');
    },
    onError: (error) => {
      toast.error('Failed to remove member', { description: getErrorMessage(error) });
    },
  });
}
